import React, { useState, useEffect } from 'react';
import { Box, Typography, Checkbox, TextField, Button, List, IconButton, Accordion, AccordionSummary, AccordionDetails, Pagination, Chip, Card, CardContent, Divider, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningIcon from '@mui/icons-material/Warning';
import SettingsIcon from '@mui/icons-material/Settings';
import { EnvironmentInfo } from '../../utils/environmentUtils';
import { apiService } from '../../services/apiService';

interface ImportSwaggerPreviewProps {
  projectId: number | null;
  parsedData: any;
  existingCollections: any[];
  environmentInfo?: EnvironmentInfo | null;
  onSubmit: (selections: any) => void;
  onBack: () => void;
  submitError?: string | null;
}

const COLLECTIONS_PER_PAGE = 10;

// Helper to convert kebab-case or snake_case to Camel Case (capitalize each word, keep hyphens)
function toCamelCase(name: string) {
  return name.replace(/(^|[-_])(\w)/g, (_: string, sep: string, c: string) => (sep ? sep : '') + c.toUpperCase());
}

// Helper to generate a sample object from a JSON schema
function schemaToSample(schema: any): any {
  if (!schema) return null;
  if (schema.type === 'object' && schema.properties) {
    const obj: any = {};
    for (const [key, prop] of Object.entries(schema.properties)) {
      obj[key] = schemaToSample(prop);
    }
    return obj;
  }
  if (schema.type === 'array' && schema.items) {
    return [schemaToSample(schema.items)];
  }
  if (schema.type === 'string') return 'string';
  if (schema.type === 'integer' || schema.type === 'number') return 0;
  if (schema.type === 'boolean') return false;
  return null;
}

function normalizeBaseUrl(url: string | null | undefined): string {
  if (url == null) return '';
  let s = String(url).trim();
  if (s.endsWith('/')) s = s.slice(0, -1);
  return s.toLowerCase();
}

const ImportSwaggerPreview: React.FC<ImportSwaggerPreviewProps> = ({ projectId, parsedData, existingCollections, environmentInfo, onSubmit, onBack, submitError }) => {
  const [selected, setSelected] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [expandedCollection, setExpandedCollection] = useState<string | null>(null);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [editingCollection, setEditingCollection] = useState<number | null>(null);
  const [editedName, setEditedName] = useState('');
  const [renameConflict, setRenameConflict] = useState<string | null>(null);
  /** Only true when we verified the env exists in the selected project; false when verified absent; null while not yet verified. */
  const [verifiedEnvironmentExists, setVerifiedEnvironmentExists] = useState<boolean | null>(null);

  // Get environment info from parsedData if available
  const environmentData = parsedData?.environments?.[0];
  const displayEnvironmentInfo = environmentData || environmentInfo;

  // Only show "Already exists" when we verified the env exists in the selected project (ignore backend isNew to avoid false positives)
  const environmentExists = Boolean(
    environmentData && verifiedEnvironmentExists === true
  );

  // Verify against the selected project's environments only (exact normalized base URL match in this project)
  useEffect(() => {
    if (!projectId || !environmentData?.baseUrl) {
      setVerifiedEnvironmentExists(environmentData?.baseUrl ? null : false);
      return;
    }
    setVerifiedEnvironmentExists(null);
    let cancelled = false;
    const swaggerBaseUrl = normalizeBaseUrl(environmentData.baseUrl);
    apiService.getEnvironmentsByProject(projectId).then((envs: { baseUrl?: string | null; url?: string | null }[]) => {
      if (cancelled) return;
      const exists = envs.some((e) => {
        const envUrl = e.baseUrl ?? e.url ?? '';
        return swaggerBaseUrl && normalizeBaseUrl(envUrl) === swaggerBaseUrl;
      });
      setVerifiedEnvironmentExists(exists);
    }).catch(() => {
      if (!cancelled) setVerifiedEnvironmentExists(false);
    });
    return () => { cancelled = true; };
  }, [projectId, environmentData?.baseUrl]);

  // Initialize selected state
  useEffect(() => {
    if (parsedData?.collections) {
      setSelected(parsedData.collections.map((col: any) => ({
        ...col,
        originalName: col.name,
        selected: !col.duplicate,
        requests: col.requests.map((req: any) => ({
          ...req,
          selected: !req.duplicate
        }))
      })));
    }
  }, [parsedData]);

  const isRenamedCollection = (col: any, name?: string) => {
    const current = (name ?? col?.name ?? '').trim();
    const original = (col?.originalName ?? col?.name ?? '').trim();
    return current !== original;
  };

  const handleCollectionCheck = (idx: number) => {
    const panel = `col-${idx}`;
    setSelected((selected: any[]) =>
      selected.map((col: any, i: number) => {
        if (i !== idx) return col;
        const nextSelected = !col.selected;
        if (!nextSelected) {
          return {
            ...col,
            selected: false,
            requests: col.requests.map((req: any) => ({ ...req, selected: false })),
          };
        }
        const renamed = isRenamedCollection(col);
        const renamedToNewCollection = renamed && !collectionNameAlreadyExists(col.name || '');
        // A renamed collection is a new collection, so its requests can be selected.
        return {
          ...col,
          duplicate: renamedToNewCollection ? false : col.duplicate,
          selected: true,
          requests: col.requests.map((req: any) => ({
            ...req,
            duplicate: renamedToNewCollection ? false : req.duplicate,
            selected: renamed ? true : !req.duplicate,
          })),
        };
      })
    );
    setExpandedCollection((prev) => (prev === panel ? null : prev));
    setExpandedRequest(null);
  };
  const handleRequestCheck = (colIdx: number, reqIdx: number) => {
    setSelected((selected: any[]) => selected.map((col: any, i: number) => i === colIdx ? {
      ...col,
      requests: col.requests.map((req: any, j: number) => j === reqIdx ? { ...req, selected: !req.selected } : req)
    } : col));
  };
  const handleEditCollection = (idx: number) => {
    setEditingCollection(idx);
    setEditedName(selected[idx].name);
  };
  const collectionNameAlreadyExists = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    return (existingCollections || []).some((col: any) => {
      if (!col || typeof col.name !== 'string') return false;
      const parentId = col.parentCollectionId ?? col.parentId ?? null;
      if (parentId != null) return false;
      return col.name.trim() === trimmed;
    });
  };

  const renamedOntoExistingCollection = (collectionsToImport: any[]) => {
    const conflicts = collectionsToImport.filter((col: any) => {
      if (!col?.selected) return false;
      const hasSelectedRequest = Array.isArray(col.requests) && col.requests.some((req: any) => req?.selected);
      if (!hasSelectedRequest) return false;
      const currentName = typeof col.name === 'string' ? col.name.trim() : '';
      const originalName = typeof col.originalName === 'string' ? col.originalName.trim() : currentName;
      return currentName !== originalName && collectionNameAlreadyExists(currentName);
    });
    if (conflicts.length === 0) return null;
    return 'Collection already exists';
  };

  const handleEditNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEditedName(value);
    setRenameConflict(null);
    // Keep selection state in sync while typing so Import uses the renamed value
    // even if the user never clicks the pencil to "confirm".
    if (editingCollection != null) {
      setSelected((prev) =>
        prev.map((col: any, i: number) => (i === editingCollection ? { ...col, name: value } : col))
      );
    }
  };
  const handleEditNameSave = (idx: number) => {
    const nameToSave = editedName;
    setSelected((selected: any[]) =>
      selected.map((col: any, i: number) => (i === idx ? { ...col, name: nameToSave } : col))
    );
    setEditingCollection(null);
    setEditedName('');
  };
  const handleSubmit = () => {
    // Flush any in-progress rename before import (user may still be editing the TextField).
    const next = editingCollection != null
      ? selected.map((col: any, i: number) =>
          i === editingCollection ? { ...col, name: editedName } : col
        )
      : selected;
    const conflict = renamedOntoExistingCollection(next);
    if (conflict) {
      if (editingCollection != null) {
        setSelected(next);
        setEditingCollection(null);
        setEditedName('');
      }
      setRenameConflict(conflict);
      return;
    }
    setRenameConflict(null);
    if (editingCollection != null) {
      setSelected(next);
      setEditingCollection(null);
      setEditedName('');
      onSubmit(next);
      return;
    }
    onSubmit(selected);
  };
  const handleCollectionAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCollection(isExpanded ? panel : null);
    setExpandedRequest(null); // Collapse any open request when switching collection
  };
  const handleRequestAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedRequest(isExpanded ? panel : null);
  };
  // Pagination logic
  const totalPages = Math.ceil(selected.length / COLLECTIONS_PER_PAGE);
  const pagedCollections = selected.slice((page - 1) * COLLECTIONS_PER_PAGE, page * COLLECTIONS_PER_PAGE);

  // Calculate summary from checked collections/requests only
  const checkedCollections = selected.filter((col: any) => col.selected);
  const totalCollections = checkedCollections.length;
  const totalRequests = checkedCollections.reduce(
    (acc: number, col: any) => acc + col.requests.filter((req: any) => req.selected).length,
    0
  );
  const duplicateCollections = checkedCollections.filter((col: any) => col.duplicate).length;
  const duplicateRequests = checkedCollections.reduce(
    (acc: number, col: any) => acc + col.requests.filter((req: any) => req.duplicate && req.selected).length,
    0
  );
  const totalEnvironments = displayEnvironmentInfo ? 1 : 0;
  const newEnvironments = displayEnvironmentInfo ? 1 : 0;

  return (
    <Box>
      {parsedData?.success === false && parsedData?.message ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {parsedData.message}
        </Alert>
      ) : null}
      {submitError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      ) : null}
      {renameConflict ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {renameConflict}
        </Alert>
      ) : null}
      {/* Environment Information */}
      {displayEnvironmentInfo && (
        <Card variant="outlined" sx={{ mb: 1.5, backgroundColor: environmentExists ? '#fff3e0' : '#f8f9fa' }}>
          <CardContent sx={{ p: '12px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
              <SettingsIcon sx={{ color: environmentExists ? 'warning.main' : 'primary.main', flexShrink: 0 }} />
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, minWidth: 0 }}>
                {environmentExists ? 'Environment Already Exists' : 'Environment to be Created'}
              </Typography>
              {environmentExists && (
                <Chip 
                  label="Already Exists" 
                  size="small" 
                  color="warning" 
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Name:</Typography>
                <Typography variant="body2" fontWeight="medium">{displayEnvironmentInfo.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Type:</Typography>
                <Chip 
                  label={displayEnvironmentInfo.environmentType} 
                  size="small" 
                  color={
                    displayEnvironmentInfo.environmentType === 'Production' ? 'error' :
                    displayEnvironmentInfo.environmentType === 'Staging' ? 'warning' :
                    displayEnvironmentInfo.environmentType === 'Development' ? 'info' :
                    'default'
                  }
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, flexShrink: 0 }}>Base URL:</Typography>
                <Typography variant="body2" fontFamily="monospace" sx={{ minWidth: 0, wordBreak: 'break-all' }}>{displayEnvironmentInfo.baseUrl}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>Description:</Typography>
                <Typography variant="body2">{displayEnvironmentInfo.description}</Typography>
              </Box>
              {environmentExists && (
                <Box sx={{ mt: 1, p: 1, backgroundColor: '#fff', borderRadius: 1, border: '1px solid #ff9800' }}>
                  <Typography variant="body2" color="warning.dark">
                    ⚠️ This environment already exists in your project. The import will use the existing environment instead of creating a new one.
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Import Summary */}
      <Card variant="outlined" sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: '12px !important' }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: '1rem', fontWeight: 700 }}>Import Summary</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Chip label={`${totalCollections} Collections`} color="primary" variant="outlined" />
            <Chip label={`${totalRequests} Requests`} color="primary" variant="outlined" />
            {displayEnvironmentInfo && (
              <Chip 
                label={environmentExists ? "1 Existing Environment" : "1 New Environment"} 
                color={environmentExists ? "warning" : "success"} 
                variant="outlined" 
              />
            )}
            {duplicateCollections > 0 && (
              <Chip label={`${duplicateCollections} Duplicate Collections`} color="warning" variant="outlined" />
            )}
            {duplicateRequests > 0 && (
              <Chip label={`${duplicateRequests} Duplicate Requests`} color="warning" variant="outlined" />
            )}
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 2 }} />

      {/* Collections List */}
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontSize: '0.875rem', fontWeight: 600 }}>
        Collections found: {totalCollections} | Requests found: {totalRequests}
        {duplicateCollections > 0 || duplicateRequests > 0 ? (
          <span style={{ color: '#f57c00' }}>
            {' '}| Duplicates: {duplicateCollections} collections, {duplicateRequests} requests
          </span>
        ) : null}
      </Typography>
      <List>
        {pagedCollections.map((col: any, colIdx: number) => {
          const globalColIdx = (page - 1) * COLLECTIONS_PER_PAGE + colIdx;
          return (
            <Accordion
              key={globalColIdx}
              expanded={col.selected && expandedCollection === `col-${globalColIdx}`}
              onChange={handleCollectionAccordionChange(`col-${globalColIdx}`)}
              sx={{ 
                mb: 1.5, 
                border: '1px solid #eee', 
                borderRadius: '10px', 
                boxShadow: 'none',
                backgroundColor:
                  (col.duplicate && !isRenamedCollection(col)) ||
                  (isRenamedCollection(col) && collectionNameAlreadyExists(col.name || ''))
                    ? '#fff3e0'
                    : '#fff',
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minWidth: 0,
                  '& .MuiAccordionSummary-content': {
                    minWidth: 0,
                    overflow: 'hidden',
                    alignItems: 'center',
                  },
                }}
              >
                <Checkbox 
                  checked={col.selected} 
                  onChange={() => handleCollectionCheck(globalColIdx)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={
                    col.duplicate &&
                    !isRenamedCollection(col, editingCollection === globalColIdx ? editedName : col.name) &&
                    !col.requests.some((req: any) => !req.duplicate)
                  }
                  sx={{ flexShrink: 0 }}
                />
                <Typography
                  variant="h6"
                  title={toCamelCase(col.name)}
                  sx={{
                    mr: 1,
                    minWidth: 0,
                    flex: 1,
                    fontSize: '0.9375rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {editingCollection === globalColIdx ? (
                    <TextField
                      value={editedName}
                      onChange={handleEditNameChange}
                      size="small"
                      sx={{ mr: 1 }}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={() => handleEditNameSave(globalColIdx)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEditNameSave(globalColIdx);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    toCamelCase(col.name)
                  )}
                </Typography>
                {((col.duplicate && !isRenamedCollection(col)) || (isRenamedCollection(col) && collectionNameAlreadyExists(col.name || ''))) && (
                  <Chip 
                    icon={<WarningIcon />} 
                    label={col.requests.some((req: any) => !req.duplicate) ? "Has New Requests" : "Already Exists"} 
                    size="small" 
                    color={col.requests.some((req: any) => !req.duplicate) ? "info" : "warning"}
                    sx={{ mr: 1 }}
                  />
                )}
                <IconButton 
                  size="small" 
                  onMouseDown={(e) => {
                    // Prevent input blur before the pencil click so confirm does not re-open edit.
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={e => { 
                    e.stopPropagation(); 
                    editingCollection === globalColIdx ? handleEditNameSave(globalColIdx) : handleEditCollection(globalColIdx); 
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </AccordionSummary>
              <AccordionDetails>
                {col.requests.map((req: any, reqIdx: number) => (
                  <Accordion
                    key={reqIdx}
                    expanded={col.selected && req.selected && expandedRequest === `req-${globalColIdx}-${reqIdx}`}
                    onChange={handleRequestAccordionChange(`req-${globalColIdx}-${reqIdx}`)}
                    sx={{ 
                      mb: 1, 
                      boxShadow: 'none', 
                      border: '1px solid #f0f0f0',
                      backgroundColor: req.duplicate ? '#fff3e0' : '#fff'
                    }}
                    disabled={req.duplicate}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        minWidth: 0,
                        '& .MuiAccordionSummary-content': {
                          minWidth: 0,
                          overflow: 'hidden',
                          alignItems: 'center',
                        },
                      }}
                    >
                      <Checkbox 
                        checked={req.selected} 
                        onChange={() => handleRequestCheck(globalColIdx, reqIdx)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={req.duplicate}
                        sx={{ flexShrink: 0 }}
                      />
                      <Typography
                        title={`${req.method} ${req.url}`}
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.875rem',
                          minWidth: 0,
                          flex: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {req.method} {req.url}
                      </Typography>
                      {req.duplicate && (
                        <Chip 
                          icon={<WarningIcon />} 
                          label="Already Exists" 
                          size="small" 
                          color="warning" 
                          sx={{ ml: 1 }}
                        />
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Request Name: {req.name}</Typography>
                      <Typography variant="body2"><b>Request Method:</b> {req.method}</Typography>
                      <Typography variant="body2"><b>Request URL:</b> {req.url}</Typography>
                      <Typography variant="body2"><b>Headers:</b> {req.headers ? JSON.stringify(req.headers) : 'N/A'}</Typography>
                      <Typography variant="body2"><b>Body:</b></Typography>
                      {req.requestBodySchema ? (
                        <pre style={{ background: '#f6f8fa', padding: 8, borderRadius: 4, fontSize: 13 }}>
                          {JSON.stringify(schemaToSample(req.requestBodySchema), null, 2)}
                        </pre>
                      ) : (
                        <Typography variant="body2">N/A</Typography>
                      )}
                      <Typography variant="body2"><b>Parameters:</b> {req.parameters ? JSON.stringify(req.parameters) : 'N/A'}</Typography>
                      <Typography variant="body2"><b>Request Body Schemas:</b> {req.requestBodySchema ? JSON.stringify(req.requestBodySchema) : 'N/A'}</Typography>
                      <Typography variant="body2"><b>Response Schemas:</b> {req.responseSchema ? JSON.stringify(req.responseSchema) : 'N/A'}</Typography>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </List>
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_e, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, gap: 1, flexWrap: 'wrap' }}>
        <Button
          onClick={onBack}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            height: 36,
            minHeight: 36,
            fontSize: '0.875rem',
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            height: 36,
            minHeight: 36,
            fontSize: '0.875rem',
            bgcolor: '#1d4ed8',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#1e40af' },
          }}
        >
          Import
        </Button>
      </Box>
    </Box>
  );
};

export default ImportSwaggerPreview; 